/**
 * CalendarPage.jsx
 * Path: frontend/src/pages/CalendarPage.jsx
 * Description: Główny harmonogram miesięczny z tworzeniem i edycją rezerwacji.
 * Related: components/MonthCalendar.jsx, components/JobFormModal.jsx
 */

import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ChevronLeft, ChevronRight, Plus } from "lucide-react";
import AppShell from "../components/AppShell";
import MonthCalendar from "../components/MonthCalendar";
import JobFormModal from "../components/JobFormModal";
import { AuthContext } from "../context/AuthContext";
import { createJob, listJobs, updateJob } from "../api/jobs";
import { listActiveUsers } from "../api/users";
import { endOfMonth, monthLabel, startOfMonth, toIsoDate } from "../utils/date";

function buildDefaultJobTitle(seedDate, jobs) {
  const date = seedDate ? new Date(`${seedDate}T12:00:00`) : new Date();
  const year = date.getFullYear();
  const nextNumber = jobs.filter((job) => new Date(job.startAt).getFullYear() === year).length + 1;
  return `Zlecenie ${nextNumber}/${year}`;
}

export default function CalendarPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { token } = useContext(AuthContext);
  const monthFromQuery = searchParams.get("month");
  const initialMonth = monthFromQuery ? new Date(`${monthFromQuery}-01T12:00:00`) : new Date();

  const [monthDate, setMonthDate] = useState(startOfMonth(initialMonth));
  const [jobs, setJobs] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [modalState, setModalState] = useState(null);

  const range = useMemo(
    () => ({
      from: toIsoDate(startOfMonth(monthDate)),
      to: toIsoDate(endOfMonth(monthDate)),
    }),
    [monthDate]
  );

  const loadData = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [jobRows, userRows] = await Promise.all([
        listJobs(token, range),
        listActiveUsers(token),
      ]);
      setJobs(jobRows);
      setUsers(userRows);
    } catch (err) {
      setError(err?.response?.data?.error || "Nie udało się pobrać harmonogramu.");
    } finally {
      setLoading(false);
    }
  }, [range, token]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    setSearchParams({ month: `${monthDate.getFullYear()}-${String(monthDate.getMonth() + 1).padStart(2, "0")}` });
  }, [monthDate, setSearchParams]);

  async function handleSave(payload) {
    setSaving(true);
    try {
      if (modalState?.job) {
        await updateJob(token, modalState.job.id, payload);
      } else {
        await createJob(token, payload);
      }
      setModalState(null);
      await loadData();
    } finally {
      setSaving(false);
    }
  }

  return (
    <AppShell
      title=""
      subtitle=""
      actions={
        <div className="toolbar-row">
          <button type="button" className="ghost-button icon-button" aria-label="Poprzedni miesiac" onClick={() => setMonthDate(startOfMonth(new Date(monthDate.getFullYear(), monthDate.getMonth() - 1, 1)))}>
            <ChevronLeft size={16} />
          </button>
          <div className="month-badge">{monthLabel(monthDate)}</div>
          <button type="button" className="ghost-button icon-button" aria-label="Nastepny miesiac" onClick={() => setMonthDate(startOfMonth(new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 1)))}>
            <ChevronRight size={16} />
          </button>
          <button type="button" className="primary-button icon-button icon-button-accent" aria-label="Nowe zlecenie" onClick={() => setModalState({ seedDate: toIsoDate(new Date()) })}>
            <Plus size={16} />
          </button>
        </div>
      }
    >
      {error ? <div className="form-error page-error">{error}</div> : null}
      {loading ? <div className="card-section">Ładowanie harmonogramu...</div> : null}
      {!loading ? (
        <div className="calendar-stage">
          <MonthCalendar
            monthDate={monthDate}
            jobs={jobs}
            onDayClick={(dateIso) => setModalState({ seedDate: dateIso })}
            onJobClick={(jobId) => navigate(`/jobs/${jobId}`)}
          />
        </div>
      ) : null}

      {modalState ? (
        <JobFormModal
          job={modalState.job || null}
          seedDate={modalState.seedDate || null}
          defaultTitle={buildDefaultJobTitle(modalState.seedDate || toIsoDate(new Date()), jobs)}
          users={users}
          saving={saving}
          onClose={() => setModalState(null)}
          onSave={handleSave}
        />
      ) : null}
    </AppShell>
  );
}
